import React, { useState } from 'react';
import { Building2, Loader2, Plus, X } from 'lucide-react';

function AddCompanyModal({ open, onClose, onSubmit, loading, error }) {
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [localError, setLocalError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setLocalError('Company name must be at least 2 characters');
      return;
    }
    setLocalError('');
    const aliasList = aliases
      .split(',')
      .map(a => a.trim())
      .filter(Boolean);
    const result = await onSubmit({ name: trimmed, aliases: aliasList });
    if (result) {
      setName('');
      setAliases('');
      onClose();
    }
  };

  const handleClose = () => {
    setName('');
    setAliases('');
    setLocalError('');
    onClose();
  };

  const displayError = localError || error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45"
        onClick={handleClose}
        aria-label="Close"
      />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-[#e1e5ec] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e1e5ec] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#e7edfc] text-[#1c4ed8] flex items-center justify-center">
              <Building2 size={20} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#0d1524]">Add company</h2>
              <p className="text-xs text-[#5b6780] mt-0.5">Starts monitoring &amp; news collection</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-[#76829a] hover:text-[#45516a] hover:bg-[#f6f7fa] transition"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="company-name" className="text-sm font-medium text-[#45516a] block mb-1.5">
              Company name <span className="text-[#bd2f2b]">*</span>
            </label>
            <input
              id="company-name"
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Tata Motors"
              className="w-full text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
            />
          </div>

          <div>
            <label htmlFor="company-aliases" className="text-sm font-medium text-[#45516a] block mb-1.5">
              Aliases <span className="text-[#76829a] font-normal">(optional)</span>
            </label>
            <input
              id="company-aliases"
              value={aliases}
              onChange={e => setAliases(e.target.value)}
              placeholder="Tata Motors Ltd, TML (comma separated)"
              className="w-full text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
            />
          </div>

          {displayError && (
            <p className="text-sm text-[#bd2f2b] bg-[#fae8e7] border border-[#f1c9c7] rounded-lg px-3 py-2">
              {displayError}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-3 text-[13px] font-medium text-[#45516a] border border-[#e1e5ec] rounded-md hover:bg-[#f6f7fa] transition disabled:opacity-50 h-8"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent rounded-md hover:brightness-110 transition disabled:opacity-50 h-8"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Adding…</>
              ) : (
                <><Plus size={16} /> Add &amp; monitor</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCompanyModal;